/* Workbook export — write-back into the customer's original file, plus the
 * synthesized summary format used when there is no original to write into.
 *
 * Split out of spreadsheet.jsx so it carries no JSX and can be exercised
 * headlessly by the test suite; spreadsheet.jsx re-exports it so existing
 * imports keep working.
 */

import XLSX from 'xlsx-js-style';
import { DEFAULT_VOCAB, roleOf, labelsOf, emptyTally } from './status-vocab.js';

/** Per-ROLE cell fill colors (traffic light). Any status the vocabulary maps to
 * a role gets that role's colour; an unmapped status falls back to amber rather
 * than to red, so an unrecognized category never reads as a hard failure. */
const FILLS = {
  met:     { bg: 'E6F4EA', fg: '137333' },
  partial: { bg: 'FEF7E0', fg: '8A6D1F' },
  gap:     { bg: 'FCE8E6', fg: 'B3261E' },
};

function fillFor(vocab, status) {
  return FILLS[roleOf(vocab, status)] || FILLS.partial;
}

const STYLE_HEADER = {
  font:  { name: 'Calibri', sz: 11, bold: true, color: { rgb: 'FFFFFF' } },
  fill:  { patternType: 'solid', fgColor: { rgb: '1F2937' } },
  alignment: { vertical: 'center', horizontal: 'left', wrapText: true },
  border: { bottom: { style: 'thin', color: { rgb: '111111' } } },
};

const STYLE_SUMMARY_TITLE = {
  font: { name: 'Calibri', sz: 16, bold: true, color: { rgb: 'C71F37' } },
};

const STYLE_SUMMARY_LABEL = {
  font: { name: 'Calibri', sz: 11, bold: true, color: { rgb: '374151' } },
  alignment: { vertical: 'center' },
};

function styledCell(value, style) {
  return { v: value, s: style };
}

function wrapText(value, extra = {}) {
  return {
    v: value,
    s: {
      alignment: { vertical: 'top', horizontal: 'left', wrapText: true },
      font: { name: 'Calibri', sz: 10 },
      ...extra,
    },
  };
}

function statusCell(status, vocab = DEFAULT_VOCAB) {
  const f = fillFor(vocab, status);
  return {
    v: status,
    s: {
      font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: f.fg } },
      fill: { patternType: 'solid', fgColor: { rgb: f.bg } },
      alignment: { vertical: 'center', horizontal: 'center' },
    },
  };
}

function responseCell(text, status, vocab = DEFAULT_VOCAB) {
  const f = fillFor(vocab, status);
  return {
    v: text || '',
    s: {
      font: { name: 'Calibri', sz: 10, color: { rgb: '111111' } },
      fill: { patternType: 'solid', fgColor: { rgb: f.bg } },
      alignment: { vertical: 'top', horizontal: 'left', wrapText: true },
    },
  };
}

function confidenceCell(conf) {
  const pct = Math.round((conf || 0) * 100);
  const color = pct >= 75 ? '137333' : pct >= 55 ? '8A6D1F' : 'B3261E';
  return {
    v: pct / 100,
    t: 'n',
    z: '0%',
    s: {
      font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: color } },
      alignment: { vertical: 'center', horizontal: 'center' },
    },
  };
}

/** Summary-style synthesized export — used when there's no original workbook to write back into. */
export function exportXlsx(rows, filename, meta = {}) {
  const wb = XLSX.utils.book_new();
  const vocab = meta.vocab || DEFAULT_VOCAB;
  const labels = labelsOf(vocab);

  // Tally keyed off the vocabulary. The old version hardcoded Met/Partial/Gap
  // and did `bySheet[r.sheet][r.status]++`, so any other status incremented an
  // undefined key and wrote NaN into the summary sheet.
  const bySheet = {};
  rows.forEach(r => {
    bySheet[r.sheet] = bySheet[r.sheet] || { total: 0, lowConf: 0, ...emptyTally(vocab) };
    bySheet[r.sheet].total++;
    if (r.status in bySheet[r.sheet]) bySheet[r.sheet][r.status]++;
    if ((r.confidence || 0) < 0.6) bySheet[r.sheet].lowConf++;
  });
  const totals = Object.values(bySheet).reduce((a, b) => {
    const out = { total: a.total + b.total, lowConf: a.lowConf + b.lowConf };
    for (const l of labels) out[l] = (a[l] || 0) + (b[l] || 0);
    return out;
  }, { total: 0, lowConf: 0, ...emptyTally(vocab) });

  const summary = [
    [styledCell('WM Synergy — RFP Response', STYLE_SUMMARY_TITLE)],
    [],
    [styledCell('Product', STYLE_SUMMARY_LABEL), meta.product || ''],
    [styledCell('Source RFP', STYLE_SUMMARY_LABEL), meta.source || ''],
    [styledCell('Generated', STYLE_SUMMARY_LABEL), new Date().toLocaleString()],
    [styledCell('Questions answered', STYLE_SUMMARY_LABEL), totals.total],
    [styledCell('Low-confidence (review queue)', STYLE_SUMMARY_LABEL), totals.lowConf],
    [],
    [
      styledCell('Section',  STYLE_HEADER),
      styledCell('Total',    STYLE_HEADER),
      ...labels.map((l) => styledCell(l, STYLE_HEADER)),
      styledCell('Low-conf', STYLE_HEADER),
    ],
  ];
  Object.keys(bySheet).forEach(k => {
    const s = bySheet[k];
    summary.push([
      k, s.total,
      ...labels.map((l) => {
        const f = fillFor(vocab, l);
        return {
          v: s[l] || 0,
          s: {
            fill: { patternType: 'solid', fgColor: { rgb: f.bg } },
            font: { color: { rgb: f.fg }, bold: true },
            alignment: { horizontal: 'center' },
          },
        };
      }),
      s.lowConf,
    ]);
  });
  summary.push([
    styledCell('TOTAL', { font: { bold: true }, border: { top: { style: 'thin', color: { rgb: '111111' } } } }),
    styledCell(totals.total, { font: { bold: true } }),
    ...labels.map((l) => styledCell(totals[l] || 0, { font: { bold: true, color: { rgb: fillFor(vocab, l).fg } } })),
    styledCell(totals.lowConf, { font: { bold: true } }),
  ]);

  const lastCol = 2 + labels.length;
  const sws = XLSX.utils.aoa_to_sheet(summary);
  sws['!cols'] = [{ wch: 30 }, { wch: 12 }, ...labels.map(() => ({ wch: 14 })), { wch: 12 }];
  sws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } }];
  XLSX.utils.book_append_sheet(wb, sws, 'Summary');

  const sections = [...new Set(rows.map(r => r.sheet))];
  sections.forEach(section => {
    const srows = rows.filter(r => r.sheet === section);
    const header = [
      styledCell('#',                     STYLE_HEADER),
      styledCell('Requirement',           STYLE_HEADER),
      styledCell('Response',              STYLE_HEADER),
      styledCell('Status',                STYLE_HEADER),
      styledCell('Confidence',            STYLE_HEADER),
      styledCell('Status rationale',      STYLE_HEADER),
      styledCell('Confidence rationale',  STYLE_HEADER),
      styledCell('RKB citations',         STYLE_HEADER),
    ];
    const data = [header];
    srows.forEach((r, i) => {
      data.push([
        i + 1,
        wrapText(r.question),
        responseCell(r.answer, r.status, vocab),
        statusCell(r.status, vocab),
        confidenceCell(r.confidence),
        wrapText(r.status_rationale || '', { font: { name: 'Calibri', sz: 9, italic: true, color: { rgb: '4B5563' } } }),
        wrapText(r.confidence_rationale || '', { font: { name: 'Calibri', sz: 9, italic: true, color: { rgb: '4B5563' } } }),
        wrapText((r.citations || []).join(', '), { font: { name: 'Consolas', sz: 9, color: { rgb: '6B7280' } } }),
      ]);
    });
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = [
      { wch: 4 }, { wch: 52 }, { wch: 64 }, { wch: 10 }, { wch: 12 },
      { wch: 40 }, { wch: 40 }, { wch: 32 },
    ];
    ws['!freeze'] = { xSplit: 0, ySplit: 1 };
    ws['!rows'] = [{ hpt: 22 }];
    for (let r = 1; r <= srows.length; r++) {
      ws['!rows'][r] = { hpt: 60 };
    }
    XLSX.utils.book_append_sheet(wb, ws, section.slice(0, 30));
  });

  XLSX.writeFile(wb, filename);
}

/**
 * Write answers back into the user's original workbook, preserving all their
 * tabs, headers, and formatting. Adds the response to the detected answer
 * column and (if absent) appends Confidence + Rationale columns to the right.
 *
 * detection: { [sheetName]: { headerRow, questionCol, answerCol, commentCol } } — 0-indexed.
 */
export function exportToOriginalWorkbook(originalWb, rows, detection, filename, opts = {}) {
  const vocab = opts.vocab || DEFAULT_VOCAB;
  // Deep-clone the workbook so we don't mutate the parsed copy the user sees in the UI.
  const wb = XLSX.utils.book_new();
  originalWb.SheetNames.forEach((name) => {
    const srcSheet = originalWb.Sheets[name];
    const cloneSheet = JSON.parse(JSON.stringify(srcSheet));
    XLSX.utils.book_append_sheet(wb, cloneSheet, name);
  });

  // Group rows by sheet for quick lookup.
  const rowsBySheet = {};
  rows.forEach((r) => {
    if (!rowsBySheet[r.sheet]) rowsBySheet[r.sheet] = [];
    rowsBySheet[r.sheet].push(r);
  });

  Object.keys(rowsBySheet).forEach((sheetName) => {
    const ws = wb.Sheets[sheetName];
    if (!ws) return;
    const det = detection[sheetName];
    if (!det) return;

    const { headerRow, questionCol, answerCol, commentCol, appendedAnswerCol } = det;
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');

    // Extend range to include new columns (Confidence, Rationale) if needed.
    const confCol = Math.max(answerCol, commentCol ?? -1) + 1;
    const ratCol = confCol + 1;
    if (range.e.c < ratCol) range.e.c = ratCol;

    // Header labels for the new columns (only set if cell currently empty).
    const setCell = (r, c, cell) => {
      const addr = XLSX.utils.encode_cell({ r, c });
      ws[addr] = cell;
    };
    const getCell = (r, c) => {
      const addr = XLSX.utils.encode_cell({ r, c });
      return ws[addr];
    };

    // When the detected answer column was already populated by the client, the
    // parser appended a fresh one instead of overwriting. Give it a header.
    if (appendedAnswerCol && !getCell(headerRow, answerCol)) {
      setCell(headerRow, answerCol, { v: 'WM Synergy Response', t: 's', s: STYLE_HEADER });
    }
    if (!getCell(headerRow, confCol)) {
      setCell(headerRow, confCol, { v: 'Confidence', t: 's', s: STYLE_HEADER });
    }
    if (!getCell(headerRow, ratCol)) {
      setCell(headerRow, ratCol, { v: 'Rationale', t: 's', s: STYLE_HEADER });
    }

    rowsBySheet[sheetName].forEach((r) => {
      if (typeof r.row !== 'number') return;
      const rowIdx = r.row; // 0-indexed row in the sheet
      // Answer cell — apply traffic-light fill
      setCell(rowIdx, answerCol, responseCell(r.answer, r.status, vocab));
      // Confidence (numeric %)
      setCell(rowIdx, confCol, confidenceCell(r.confidence));
      // Rationale — short combined line
      const rat = [
        r.status_rationale,
        r.confidence_rationale,
        (r.citations || []).length ? 'cites: ' + r.citations.join(', ') : '',
      ].filter(Boolean).join(' · ');
      setCell(rowIdx, ratCol, wrapText(rat, { font: { name: 'Calibri', sz: 9, italic: true, color: { rgb: '4B5563' } } }));
    });

    ws['!ref'] = XLSX.utils.encode_range(range);
  });

  const notes = buildNotesSheet(rows, detection, vocab, opts);
  if (notes) XLSX.utils.book_append_sheet(wb, notes, 'WM Synergy Response Notes');

  XLSX.writeFile(wb, filename);
}

/**
 * A short sheet explaining what we did to the customer's workbook: the answer
 * categories used, any column we appended rather than overwrote, sheets we
 * detected but did not answer, and the rows still needing a human.
 */
function buildNotesSheet(rows, detection, vocab, opts = {}) {
  const aoa = [
    [styledCell('WM Synergy — Response Notes', STYLE_SUMMARY_TITLE)],
    [],
    [styledCell('Product', STYLE_SUMMARY_LABEL), opts.product || ''],
    [styledCell('Generated', STYLE_SUMMARY_LABEL), new Date().toLocaleString()],
    [styledCell('RKB version', STYLE_SUMMARY_LABEL), opts.rkbVersion || ''],
    [styledCell('Model', STYLE_SUMMARY_LABEL), opts.model || ''],
    [],
    [styledCell('Answer categories used', STYLE_HEADER), styledCell('Meaning', STYLE_HEADER)],
  ];
  for (const c of vocab.categories) {
    aoa.push([statusCell(c.label, vocab), wrapText(c.definition || '')]);
  }
  aoa.push([]);
  aoa.push([styledCell(
    vocab.source === 'default'
      ? 'Categories are WM Synergy defaults — this RFP did not mandate its own.'
      : "Categories were taken from this RFP's own response instructions.",
    STYLE_SUMMARY_LABEL,
  )]);
  aoa.push([]);

  // Columns we appended rather than overwrote.
  const appended = Object.entries(detection).filter(([, d]) => d.appendedAnswerCol);
  aoa.push([styledCell('Changes to your workbook', STYLE_HEADER)]);
  if (appended.length) {
    for (const [sheet, d] of appended) {
      aoa.push([wrapText(
        `${sheet}: your response column already contained data, so answers were written to a new `
        + `"WM Synergy Response" column (${XLSX.utils.encode_col(d.answerCol)}) instead. Nothing you `
        + `had entered was overwritten.`
      )]);
    }
  } else {
    aoa.push([wrapText('Answers were written into the existing response column on every sheet.')]);
  }
  const truncated = Object.entries(detection).filter(([, d]) => d.truncated);
  for (const [sheet] of truncated) {
    aoa.push([wrapText(`${sheet}: sheet exceeds the row ceiling — rows beyond the limit were not answered.`)]);
  }
  aoa.push([]);

  // Sheets detected but deliberately not answered.
  const skipped = opts.skippedSheets || [];
  aoa.push([styledCell('Sheets detected but not answered', STYLE_HEADER), styledCell('Reason', STYLE_HEADER)]);
  if (skipped.length) {
    for (const sk of skipped) aoa.push([sk.sheet, wrapText(sk.reason)]);
  } else {
    aoa.push(['(none)', '']);
  }
  aoa.push([]);

  // Open items — anything a human still has to touch.
  const needsInput = rows.filter((r) => /^\[NEEDS YOUR INPUT/.test(r.answer || ''));
  const lowConf = rows.filter((r) => (r.confidence || 0) < 0.6 && !/^\[NEEDS YOUR INPUT/.test(r.answer || ''));
  aoa.push([styledCell('Open items', STYLE_HEADER)]);
  aoa.push([wrapText(`${needsInput.length} answer(s) need account-team input and are prefixed "[NEEDS YOUR INPUT — …]".`)]);
  aoa.push([wrapText(`${lowConf.length} answer(s) scored below 0.60 confidence and are in the review queue.`)]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 34 }, { wch: 90 }];
  ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }];
  return ws;
}
